import React, { useEffect } from 'react';
import { StyleSheet } from 'react-native';
import { Box, Rating, RatingProps, Typography } from '@mui/material';
import { useThemeContext } from '../../hook/useThemeManager';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { ColorScheme, theme } from '../../constants/theme';
import StarBorderIcon from '@mui/icons-material/StarBorder';

interface IStarRating {
    label?: string;
    onChange?: (event: React.SyntheticEvent<Element, Event>, value: number | null) => void;
    disabled?: boolean;
    style?: React.CSSProperties;
    ratingProps?: RatingProps;
    defaultValue?: number;
    iconSize?: number | string; // <-- added prop to control icon size
}

const StarRating: React.FC<IStarRating> = ({
    label,
    onChange,
    disabled,
    style,
    ratingProps,
    defaultValue,
    iconSize,
}) => {
    const [value, setValue] = React.useState<number | null>(0);

    const { themeStyle } = useThemeContext();
    const styles = dynamicStyles(themeStyle);

    useEffect(() => {
        if (defaultValue) setValue(defaultValue);
    }, [defaultValue]);

    return (
        <Box style={{ ...styles.container, ...style }}>
            <Box style={styles.flexRow}>
                {!disabled && <Typography style={styles.text}>{label}</Typography>}
                <Rating
                    defaultValue={defaultValue}
                    precision={0.1}
                    value={value}
                    onChange={(event, newValue) => {
                        setValue(newValue);
                        if (onChange) onChange(event, newValue);
                    }}
                    sx={{
                        color: theme[themeStyle].highlight,
                        fontSize: iconSize || 24, // apply dynamic font size
                    }}
                    emptyIcon={
                        <StarBorderIcon
                            style={{
                                color: theme[themeStyle].tag_white,
                                fontSize: 'inherit', // inherit from Rating's fontSize
                                strokeWidth: '0.7px',
                            }}
                        />
                    }
                    {...ratingProps}
                />
            </Box>
        </Box>
    );
};

const dynamicStyles = (colorScheme: ColorScheme) =>
    StyleSheet.create({
        container: {
            width: '300px',
            height: '60px',
            borderRadius: 20,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            paddingLeft: 14,
            paddingRight: 14,
        },
        flexRow: {
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
        },
        text: {
            fontSize: 16,
            fontWeight: '500',
            fontFamily: FONT_ENUM.MAIN_MEDIUM,
            textAlign: 'left',
            color: theme[colorScheme].primary_white,
            textTransform: 'capitalize',
        },
    });

export { StarRating };
